<template>
  <div class="artist-page content">
    <article
      class="artist-content-wrapper"
      :style="{ borderTopColor: secondaryColor }"
    >
      <section class="main-column">
        <div class="artist-header">
          <ResponsiveImage
            :image="bioImage"
            :alt-text="artist.title + ' profile'"
            hl="300" wl="600" hs="300" ws="400"
            align="center"
            class="artist-banner-image"
          />
          <div class="artist-intro-card">
            <h1 class="artist-name tulum-party-title-alt">{{ artist.title }}</h1>
            <p v-if="artist.metadata && artist.metadata.headline" class="artist-headline">
              {{ artist.metadata.headline }}
            </p>
          </div>
        </div>

        <section class="artist-details-section">
          <div class="social-links-container ">
            <a v-if="artist.metadata && artist.metadata.spotify_id"
               :href="'https://open.spotify.com/artist/' + artist.metadata.spotify_id" target="_blank" rel="noopener noreferrer" class="social-link">
              <img v-lazy="spotifyLogo" alt="Spotify" class="social-icon">
            </a>
            <a v-if="artist.metadata && artist.metadata.instagram"
               :href="artist.metadata.instagram" target="_blank" rel="noopener noreferrer" class="social-link">
              <img v-lazy="instagramLogo" alt="Instagram" class="social-icon">
            </a>
            <a v-if="artist.metadata && artist.metadata.soundcloud"
               :href="artist.metadata.soundcloud" target="_blank" rel="noopener noreferrer" class="social-link">
              <img v-lazy="soundcloudLogo" alt="SoundCloud" class="social-icon">
            </a>
          </div>

          <div v-if="artist.content" v-html="artist.content" class="artist-bio-content text-left"></div>
        </section>

        <section v-if="incomingParties.length > 0"
                 class="upcoming-artist-event-section text-center"
                 :style="{ background: `linear-gradient(${secondaryColor}, black)` }"
        >
          <h3 class="section-title">Next Performance</h3>
          <IncomingParty
            :incoming-party="incomingParties[0]"
            class="centered-card"
            :logo="bioImage"
            :color="secondaryColor"
          />
        </section>

        <UpcomingEvents
          v-if="incomingParties.length > 0"
          :club-color="thirdColor"
          :incoming-club-parties="incomingParties.slice(0, 5)"
          title-prefix="More Upcoming Gigs for"
          :title-main="artist.title"
          class="additional-upcoming-events"
        />

        <section v-if="artist.metadata && artist.metadata.short"
                 :style="{ borderTopColor: secondaryColor }"
                 class="about-artist-section card">
          <h4 class="section-title">About {{ artist.title }}</h4>
          <div v-html="artist.metadata.short"></div>
        </section>

        <section v-if="pastParties.length > 0"
                 :style="{ borderTopColor: secondaryColor }"
                 class="past-events-section card">
          <h4 class="section-title">Past Events</h4>
          <p class="past-events-subtitle">Tulum, Mexico</p>
          <div class="past-parties-container">
            <div v-if="pastParties.length >= 6" class="past-parties-carousel text-left">
              <router-link
                v-for="event in pastParties"
                :key="event.slug + '-past'"
                :to="{ name: 'Event', params: { slug: event.slug } }"
              >
                <img
                  class="carrousel-image"
                  v-lazy="`${event.metadata.main_image.imgix_url}?w=60&h=60&fit=clamp`"
                  :alt="'Past Event: ' + event.title"
                />
              </router-link>
            </div>
            <div v-else class="past-parties-list text-left">
              <MiniEventCard
                v-for="(event, idx) in pastParties"
                :key="event._id || event.slug + idx + '-past-card'"
                v-bind:event="event"
                with-venue="true"
                class="card-animation-on-hover animation-transition-general animate-bounce"
                short="true"
                past="true"
                :idx="idx"
              />
            </div>
          </div>
        </section>

        <section v-if="artist.metadata && artist.metadata.spotify_id"
                 :style="{ borderTopColor: secondaryColor }"
                 class="spotify-tracks-section card">
          <h4 class="section-title">Top Tracks on Spotify</h4>
          <p v-if="artist.metadata.spotify_listeners"
             class="spotify-listeners"
             :style="{ backgroundColor: secondaryColor }">
            Monthly Listeners: {{ artist.metadata.spotify_listeners.toLocaleString() }}
          </p>
          <iframe
            class="spotify-embed"
            :src="`https://open.spotify.com/embed/artist/${artist.metadata.spotify_id}?utm_source=generator&theme=0`"
            width="100%"
            height="380"
            frameBorder="0"
            allowfullscreen=""
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            loading="lazy"
            title="Spotify Tracks Embed"
          ></iframe>
        </section>

        <section v-if="artist.metadata && artist.metadata.videoid"
                 :style="{ borderTopColor: secondaryColor }"
                 class="artist-video-section card">
          <h4 class="section-title">Featured Video</h4>
          <div class="video-card-container">
            <VideoCard
              :video-id="artist.metadata.videoid"
              mtv
              :title="artist.metadata.videotitle"
              :caption="artist.metadata.videocaption"
            />
          </div>
        </section>

        <SmartMenu
          :links="links"
          :logo="bioImage"
          :with-rsvp="false"
          :primary-color="primaryColor"
          class="bottom-smart-menu"
        />
      </section>

      <aside class="sidebar-column d-none d-lg-block text-left">
        <div class="sidebar-sticky-content">
          <h3 class="sidebar-title">
            <img v-lazy="logo" alt="Tulum Party Logo" class="sidebar-logo">
            Upcoming
          </h3>
          <HotThisWeek :primary-color="primaryColor" align="text-center" class="sidebar-hot-this-week"/>
          <router-link :to="{ path: '/tulum-dj-schedule' }" class="sidebar-dj-schedule-link">
            <img
              v-lazy="djImage"
              alt="DJ presenting links & resources"
              class="sidebar-dj-image"
            />
          </router-link>
          <p class="sidebar-promo-text">
            Few places truly square up to Tulum in terms of its exclusivity and range of prolific high-profile DJs and Producers showing up in town week by week.
          </p>
          <hr class="sidebar-divider">
          <DjBioCollection />
        </div>
      </aside>
    </article>
  </div>
</template>

<script>
import moment from "moment";
import VideoCard from '@/components/Cards/VideoCard';
import SmartMenu from "@/components/SmartMenu/SmartMenu";
import MiniEventCard from "@/components/EventCard/MiniEventCard";
import IncomingParty from "@/components/UpcomingEvents/IncomingParty";
import HotThisWeek from "@/pages/TulumDjSchedule/HotThisWeek";
import UpcomingEvents from "@/components/UpcomingEvents/UpcomingEvents";
import ResponsiveImage from '@/components/HeadImage/ResponsiveImage';
import DjBioCollection from '@/components/Cards/DjBioCollection';
import { slugUtils } from '@/common/slugUtils';
import { eventDateMixin } from '@/common/eventDateMixin'; // Added for date formatting

export default {
  name: 'Artist',
  props: ['slug'],
  mixins: [slugUtils, eventDateMixin], // Added eventDateMixin
  metaInfo() {
    const artistName =  this.getTitleFromSlug(this.slug);
    const headline = this.artist && this.artist.metadata ? this.artist.metadata.headline : '';
    const caption = this.artist && this.artist.metadata ? this.artist.metadata.caption : '';
    const pageTitle = `${artistName} · Artist Profile & Events`;
    const metaDescription = `${artistName}. ${headline || caption || 'Discover upcoming events and learn more about the artist.'}`;

    return {
      title: pageTitle,
      meta: [
        { name: 'description', content: metaDescription },
        { name: 'keywords', content: `${artistName}, Tulum, DJ, events, music, artist profile, ${headline}` },
        { property: 'og:title', content: pageTitle },
        { property: 'og:url', content: `https://tulum.party/djs/${this.slug}` },
        { property: 'og:description', content: metaDescription },
        { property: 'og:type', content: 'profile' }, // Changed from 'article' to 'profile' for an artist page
        { property: 'og:image', content: this.bioImage } // Ensure bioImage is available
      ],
    };
  },
  data: () => {
    return {
      // djImg: "https://imgix.cosmicjs.com/9f39a6a0-c70a-11ee-9ce5-59949019255e-dj.png", // Seems unused
      primaryColor: '#1c1a1a',
      secondaryColor: '#302d2c',
      thirdColor: '#e88659',
      djImage: 'https://imgix.cosmicjs.com/9f39a6a0-c70a-11ee-9ce5-59949019255e-dj.png',
      logo: 'https://imgix.cosmicjs.com/9c4cf3d0-e0c6-11ef-bee4-3bb1d3c55332-tp-logo-white.png',
      spotifyLogo: 'https://imgix.cosmicjs.com/dab2a390-eab3-11ee-b074-b5c8fe3ef189-spotify.jpg',
      instagramLogo: 'https://imgix.cosmicjs.com/6d099b90-5c09-11ec-a8a3-53f360c99be6-instagram.png',
      soundcloudLogo: 'https://imgix.cosmicjs.com/9c356f90-eab2-11ee-b074-b5c8fe3ef189-Danleech-Simple-Soundcloud.1024.png'
    };
  },
  created() {
    this.$store.dispatch('getArtist', { slug: this.slug });
    this.$store.dispatch('getLinks', { slug: 'tulum-nightlife' }); // Or a more relevant slug for artists
  },
  computed: {
    artist() { // Simplified from original
      return this.$store.state.artist ;
    },
    links() { // Simplified
      return this.$store.state.links || [];
    },
    bioImage() {
      return (this.artist && this.artist.metadata && this.artist.metadata.main_image && this.artist.metadata.main_image.imgix_url) ||
        'https://imgix.cosmicjs.com/3d235160-ccd1-11ee-9ce5-59949019255e-loading.jpeg' ; // Fallback image
    },
    incomingParties() {
      const parties = (this.artist && this.artist.metadata && this.artist.metadata.parties) || [];
      const now = moment().startOf('day');
      return [...parties]
        .filter(p => p && p.metadata && p.metadata.date && moment.utc(p.metadata.date).endOf('day') >= now)
        .sort((a, b) => {
          const timeA = a.metadata.start_time || '00:00';
          const timeB = b.metadata.start_time || '00:00';
          return moment.utc(`${a.metadata.date}T${timeA}`).valueOf() - moment.utc(`${b.metadata.date}T${timeB}`).valueOf();
        });
    },
    pastParties() {
      const parties = (this.artist && this.artist.metadata && this.artist.metadata.parties) || [];
      const now = moment().startOf('day');
      return [...parties]
        .filter(p => p && p.metadata && p.metadata.date && moment.utc(p.metadata.date).endOf('day') < now)
        .sort((a, b) => moment(b.metadata.date).valueOf() - moment(a.metadata.date).valueOf()); // Sort descending by date
    }
  },
  components: {
    DjBioCollection,
    ResponsiveImage,
    UpcomingEvents,
    HotThisWeek,
    IncomingParty,
    MiniEventCard,
    VideoCard,
    SmartMenu,
  },
};
</script>

